import {
  Box,
  Card,
  Collapse,
  Flex,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo, useState } from "react";
import {
  ChevronUp,
  Clock,
  Moon,
  Sun,
  Sunrise,
  Sunset,
} from "tabler-icons-react";

// Register dayjs plugins
dayjs.extend(isBetween);
dayjs.locale("fr");

// ── HourlyMetricCard ──────────────────────────────────────────────────────────
export const HourlyMetricCard = ({
  title,
  value,
  subtitle,
  percentage,
  icon,
  color,
}) => {
  const borderColors = {
    orange: "#fb923c",
    yellow: "#facc15",
    violet: "#a78bfa",
    indigo: "#818cf8",
  };
  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      sx={{ borderLeft: `3px solid ${borderColors[color] ?? "#e0e0e0"}` }}
    >
      <Group spacing="xs" mb="xs">
        <ThemeIcon color={color} variant="light" size="md" radius="xl">
          {icon}
        </ThemeIcon>
        <Text size="sm" weight={600} color="dimmed">
          {title}
        </Text>
      </Group>
      <Text size={28} weight={800} lh={1.1} mb={2}>
        {value}
      </Text>
      <Text size="xs" color="dimmed" mb={4}>
        {subtitle}
      </Text>
      {percentage !== undefined && (
        <Text size="sm" weight={600} color={color}>
          {percentage.toFixed(1)}%
        </Text>
      )}
    </Card>
  );
};

// ── Hook ─────────────────────────────────────────────────────────────────────
export const useHourlyUsageData = (
  parentsData,
  dateRange,
  provenanceFilter = "all",
) => {
  const hourlyUsageData = useMemo(() => {
    const { start, end } = dateRange;
    const hourCounts = new Array(24).fill(0);
    let totalUsage = 0;

    if (parentsData?.data) {
      parentsData.data.forEach((parent) => {
        if (
          provenanceFilter !== "all" &&
          (!parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase())
        ) {
          return;
        }
        const usageDate = parent.last_credit_usage;
        if (!usageDate) return;
        const date = dayjs(usageDate);
        if (!date.isValid()) return;
        if (date.isBetween(start, end, "day", "[]")) {
          const hour = date.hour();
          hourCounts[hour]++;
          totalUsage++;
        }
      });
    }

    const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);
    const maxCount = Math.max(...hourCounts, 1);

    // Top 3 peak hours (sorted desc)
    const sorted = hourCounts
      .map((v, i) => ({ h: i, v }))
      .sort((a, b) => b.v - a.v);
    const peakHours = sorted.slice(0, 3).map((x) => x.h);
    const peakHour = peakHours[0];
    const peakCount = hourCounts[peakHour];

    const morningUsage = hourCounts.slice(6, 12).reduce((a, b) => a + b, 0);
    const afternoonUsage = hourCounts.slice(12, 18).reduce((a, b) => a + b, 0);
    const eveningUsage = hourCounts.slice(18, 24).reduce((a, b) => a + b, 0);
    const nightUsage = hourCounts.slice(0, 6).reduce((a, b) => a + b, 0);

    return {
      labels,
      chartValues: hourCounts,
      maxCount,
      totalUsage,
      peakHour,
      peakCount,
      peakHours,
      morningUsage,
      afternoonUsage,
      eveningUsage,
      nightUsage,
    };
  }, [dateRange, parentsData, provenanceFilter]);

  return hourlyUsageData;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
/**
 * Returns a blue shade (light → dark) based on ratio in [0,1].
 * Faible < 0.3  → #bfdbfe (blue-200)
 * Moyen  < 0.65 → #60a5fa (blue-400)
 * Élevé  ≥ 0.65 → #2563eb (blue-600)
 */
function heatColor(ratio) {
  if (ratio < 0.3) return "#bfdbfe";
  if (ratio < 0.65) return "#60a5fa";
  return "#2563eb";
}

function heatLabel(ratio) {
  if (ratio < 0.3) return "Faible";
  if (ratio < 0.65) return "Moyen";
  return "Élevé";
}

// ── HeatCell ─────────────────────────────────────────────────────────────────
const HeatCell = ({ hour, count, maxCount }) => {
  const ratio = maxCount > 0 ? count / maxCount : 0;
  const bg = heatColor(ratio);
  const label = heatLabel(ratio);

  return (
    <Tooltip
      label={
        <Stack spacing={2}>
          <Text size="xs" weight={600}>
            {`${String(hour).padStart(2, "0")}h`}
          </Text>
          <Text size="xs">
            {count} utilisation{count !== 1 ? "s" : ""}
          </Text>
          <Text size="xs" color="dimmed">
            {label}
          </Text>
        </Stack>
      }
      withArrow
    >
      <Stack spacing={4} align="center" sx={{ flex: 1 }}>
        <Box
          sx={{
            width: "100%",
            aspectRatio: "1",
            borderRadius: 8,
            backgroundColor: bg,
            minHeight: 56,
            cursor: "default",
            transition: "opacity 0.15s",
            "&:hover": { opacity: 0.8 },
          }}
        />
        <Text size="xs" color="dimmed" align="center">
          {`${String(hour).padStart(2, "0")}h`}
        </Text>
      </Stack>
    </Tooltip>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AppUsageByHourChart = ({
  parentsData,
  isLoading,
  dateRange,
  showCards = true,
  provenanceFilter = "all",
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const d = useHourlyUsageData(parentsData, dateRange, provenanceFilter);

  const row1 = Array.from({ length: 12 }, (_, i) => i); // 0h–11h
  const row2 = Array.from({ length: 12 }, (_, i) => i + 12); // 12h–23h

  const peakLabel = d.peakHours.map((h) => `${h}h`).join(", ");

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Group position="apart" mb={4}>
        <Stack spacing={2}>
          <Text size="xl" weight={800}>
            Analyse Horaire
          </Text>
          <Text size="sm" color="dimmed">
            Optimiser les notifications et campagnes
          </Text>
        </Stack>
        <UnstyledButton onClick={() => setCollapsed((c) => !c)}>
          <ChevronUp
            size={20}
            style={{
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              color: "#9ca3af",
            }}
          />
        </UnstyledButton>
      </Group>

      <Collapse in={!collapsed}>
        {isLoading ? (
          <Flex justify="center" align="center" h={200}>
            <Loader size="lg" />
          </Flex>
        ) : (
          <>
            {/* ── Cartes plages horaires ────────────────────────────────── */}
            <SimpleGrid
              cols={4}
              spacing="md"
              mt="lg"
              mb="xl"
              breakpoints={[
                { maxWidth: "lg", cols: 2 },
                { maxWidth: "sm", cols: 1 },
              ]}
            >
              <HourlyMetricCard
                title="Matin"
                value={d.morningUsage}
                subtitle="6h - 12h"
                percentage={
                  d.totalUsage > 0 ? (d.morningUsage / d.totalUsage) * 100 : 0
                }
                icon={<Sunrise size={16} />}
                color="orange"
              />
              <HourlyMetricCard
                title="Après-midi"
                value={d.afternoonUsage}
                subtitle="12h - 18h"
                percentage={
                  d.totalUsage > 0 ? (d.afternoonUsage / d.totalUsage) * 100 : 0
                }
                icon={<Sun size={16} />}
                color="yellow"
              />
              <HourlyMetricCard
                title="Soirée"
                value={d.eveningUsage}
                subtitle="18h - 24h"
                percentage={
                  d.totalUsage > 0 ? (d.eveningUsage / d.totalUsage) * 100 : 0
                }
                icon={<Sunset size={16} />}
                color="violet"
              />
              <HourlyMetricCard
                title="Nuit"
                value={d.nightUsage}
                subtitle="0h - 6h"
                percentage={
                  d.totalUsage > 0 ? (d.nightUsage / d.totalUsage) * 100 : 0
                }
                icon={<Moon size={16} />}
                color="indigo"
              />
            </SimpleGrid>

            {/* ── Heatmap ──────────────────────────────────────────────── */}
            <Group position="apart" mb="sm">
              <Text size="md" weight={700}>
                Heatmap d'utilisation par heure
              </Text>
              <Group spacing={6}>
                <Clock size={16} color="#9ca3af" />
                <Text size="sm" color="dimmed">
                  Pics: {peakLabel}
                </Text>
              </Group>
            </Group>

            {/* Rangée 1 : 00h–11h */}
            <Flex gap={6} mb={6}>
              {row1.map((h) => (
                <HeatCell
                  key={h}
                  hour={h}
                  count={d.chartValues[h]}
                  maxCount={d.maxCount}
                />
              ))}
            </Flex>

            {/* Rangée 2 : 12h–23h */}
            <Flex gap={6} mb="md">
              {row2.map((h) => (
                <HeatCell
                  key={h}
                  hour={h}
                  count={d.chartValues[h]}
                  maxCount={d.maxCount}
                />
              ))}
            </Flex>

            {/* Légende */}
            <Flex justify="center" gap="lg">
              {[
                { bg: "#e0eaff", label: "Faible" },
                { bg: "#60a5fa", label: "Moyen" },
                { bg: "#2563eb", label: "Élevé" },
              ].map(({ bg, label }) => (
                <Group key={label} spacing={6}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: bg,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                  <Text size="xs" color="dimmed">
                    {label}
                  </Text>
                </Group>
              ))}
            </Flex>
          </>
        )}
      </Collapse>
    </Card>
  );
};

export default AppUsageByHourChart;
